import React, { useMemo } from "react";
import classnames from "classnames";
import { useGlobal } from "reactn";
import { Link } from "react-router-dom";
import { getRiotAssetsContext, getS13RankUrl } from "@outplayed/riot-assets";
import MediaQuery from "components/MediaQuery";
import { Rune, SummonerSpell } from "@outplayed/tooltips";
import { selectRank, getProBuildRankName, getTierColor } from "@ugg/shared/utils/rank-helpers";
import ItemsList from "components/common/ItemsList";
import { normalizePatch, getKdaColor, calculateKDA, thousandCondense, numberWithCommas, hasPerfScore } from "lib/general-helper";
import { useLightMode } from "hooks/general-hooks";
import { ReactComponent as MedalIcon } from "svg/medal-icon.svg";

const MobileSummonerRow = (props) => {
  const { getChampionImgFromSprite } = getRiotAssetsContext();
  const { className, rowProps, teamInfo, patch } = props;
  const { matchDuration, rank, itemList, isProfileOwner } = rowProps;

  const summonerRowClassNames = classnames(
    "summoner-row",
    "summoner-row_mobile",
    rowProps.winString,
    rowProps.winClass,
    className,
  );

  const totalCS = teamInfo.cs + teamInfo.jungleCs;
  const csPerMinute = (Math.round((totalCS / (matchDuration / 60)) * 10) / 10).toFixed(1);

  return (
    <div className={summonerRowClassNames}>
      <div className="col-1">
        <div className="champion-container">
          <div className="champion-face">{getChampionImgFromSprite(teamInfo.championId, { size: 36 })}</div>
          <div className="champion-level">{teamInfo.level}</div>
        </div>
        <div className="summoner-spells">
          <div className="summoner-spell">
            <SummonerSpell spellId={teamInfo.summonerSpells[0]} />
          </div>
          <div className="summoner-spell">
            <SummonerSpell spellId={teamInfo.summonerSpells[1]} />
          </div>
        </div>
        <div className="runes">
          <div className="rune">
            <Rune runeId={teamInfo.keystone} />
          </div>
          <div className="rune">
            <Rune runeId={teamInfo.subStyle} />
          </div>
        </div>
        <div>
          <div className="player">
            <div className="player-info">
              {rank.tier !== "unranked" && (
                <div className="player-rank" style={{ color: getTierColor(rank.tier.toLowerCase()) }}>
                  {getProBuildRankName(rank.tier, rank.rank, true)}
                </div>
              )}
              <Link
                to={rowProps.profileUrl}
                className={classnames("summoner-name", rowProps.isProfileOwner ? "is-profile-owner" : "")}
              >
                {teamInfo.summonerName}
              </Link>
            </div>
            <div className="kda">
              <div className="counts">
                {teamInfo.kills} <span className="slash">/</span> <span className="deaths">{teamInfo.deaths}</span>{" "}
                <span className="slash">/</span> {teamInfo.assists}
              </div>
              <span>
                <span className="ratio" style={{ color: rowProps.kdaColor }}>
                  {rowProps.kda}
                </span>{" "}
                <span className="kda-constant">KDA</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-2">
        <div className="items-list">
          <div className="horizontal-items">
            <ItemsList
              items={itemList}
              winConditionClass={rowProps.winClass}
              patch={patch}
              dim={16}
              isProfileOwner={isProfileOwner}
            />
          </div>
        </div>
        <div className="other-stats">
          <div>
            <span className="cs">
              {totalCS} ({csPerMinute})
            </span>
          </div>
          <div className="damage-bar">
            <div
              style={{ width: `${rowProps.damagePerc * 100}%` }}
              className={classnames("damage-percent", {
                "is-loss": !rowProps.win,
                //"is-profile-owner": rowProps.isProfileOwner
              })}
            />
            <div className="damage-text">{thousandCondense(teamInfo.damage)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DesktopSummonerRow = (props) => {
  const { getChampionImgFromSprite } = getRiotAssetsContext();
  const [windowBreakpoint] = useGlobal("responsive");
  const { className, rowProps, teamInfo, showPerfScore, matchId, patch } = props;
  const { rank, itemList, isProfileOwner } = rowProps;

  const result = rowProps.win ? "win" : "loss";
  const summonerRowClassNames = classnames(
    "summoner-row",
    "summoner-row_desktop",
    !showPerfScore && "summoner-row_hide-perf",
    rowProps.winString,
    rowProps.winClass,
    className,
  );

  return (
    <div className={summonerRowClassNames}>
      <div className="player">
        <div className="champion-container">
          <div className="champion-face">{getChampionImgFromSprite(teamInfo.championId, { size: 36 })}</div>
          <div className="champion-level">{teamInfo.level}</div>
        </div>
        <div className="summoner-spells">
          <div className="summoner-spell">
            <SummonerSpell spellId={teamInfo.summonerSpells[0]} />
          </div>
          <div className="summoner-spell">
            <SummonerSpell spellId={teamInfo.summonerSpells[1]} />
          </div>
        </div>
        <div className="runes">
          <div className="rune">
            <Rune runeId={teamInfo.keystone} />
          </div>
          <div className="rune">
            <Rune runeId={teamInfo.subStyle} />
          </div>
        </div>
        <div className="player-name-and-rank">
          <Link title={teamInfo.summonerName} className="summoner-name" to={rowProps.profileUrl}>
            {teamInfo.summonerName}
          </Link>
          <div className="rank-section">
            <img className="rank-img" src={rowProps.rankImgSrc} />
            <div className="rank-text">{getProBuildRankName(rank.tier, rank.rank, true)}</div>
          </div>
        </div>
      </div>
      {showPerfScore && (
        <div className={classnames("carry-score", { "carry-score_highest": teamInfo.hasHighestCarry })}>
          {teamInfo.carryScore || "-"}
          {teamInfo.hasHighestCarry && <MedalIcon className="medal-icon" />}
        </div>
      )}
      <div className="kda">
        <div className="counts">
          {teamInfo.kills} <span className="slash">/</span> <span className="deaths">{teamInfo.deaths}</span>{" "}
          <span className="slash">/</span> {teamInfo.assists}
        </div>
        <div className="label">
          <span className="ratio" style={{ color: rowProps.kdaColor }}>
            {rowProps.kda}
          </span>
          &nbsp;KDA
        </div>
      </div>
      <div className="damage">
        <div className="amount">{teamInfo.damage.toLocaleString()}</div>
        <div className="damage-bar">
          <div className="top-bar" style={{ width: `${rowProps.damagePerc * 100}%` }} />
          <div className={`grey-bar ${result}`} style={{ width: `${(1 - rowProps.damagePerc) * 100}%` }} />
        </div>
      </div>
      <div style={{ color: "#CDDCFE" }}>{thousandCondense(teamInfo.gold)}</div>
      <div className="cs">{teamInfo.cs + teamInfo.jungleCs}</div>
      <div className="wards">{teamInfo.wardsPlaced}</div>
      <div className="items-list">
        <div className="grid-items">
          <div className="main-items">
            <ItemsList
              items={itemList.slice(0, 6)}
              winConditionClass={rowProps.winClass}
              patch={patch}
              dim={16}
              isProfileOwner={isProfileOwner}
            />
          </div>
          <div>
            <ItemsList
              items={[itemList[itemList.length - 1]]}
              winConditionClass={rowProps.winClass}
              patch={patch}
              dim={16}
              isProfileOwner={isProfileOwner}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SummonerRow = (props) => {
  const { lightModeOn } = useLightMode();

  const {
    championId,
    summonerName,
    summonerSpells,
    keystone,
    subStyle,
    gold,
    wardsPlaced,
    cs,
    items,
    kills,
    assists,
    deaths,
    damage,
    jungleCs,
  } = props.teamInfo;
  const { showPerfScore, matchDuration, maxDamage, regionId, profileName, win, matchId } = props;

  const itemList = useMemo(() => {
    var count = 0;
    const list = Array(6).fill(0);
    items.forEach((e, i) => {
      if (e !== 0 && i < items.length - 1) {
        list[count++] = e;
      }
    });
    list.push(items[items.length - 1]);
    return list;
  }, [items]);

  const kda = calculateKDA(kills || 0, deaths || 0, assists || 0);
  const rank = props.rank || { rank: "i", tier: "unranked" };

  const rowProps = {
    matchDuration,
    rank,
    damagePerc: damage / maxDamage,
    rankImgSrc: getS13RankUrl(rank.tier, true),
    kda,
    kdaColor: getKdaColor(kda, lightModeOn),
    win,
    winClass: win ? "" : "is-loss",
    itemList,
    profileOwnerString: summonerName === profileName ? "is-profile-owner" : "",
    isProfileOwner: summonerName === profileName,
    profileUrl: `/lol/profile/${regionId.toLowerCase()}/${summonerName.toLowerCase()}`,
    winString: summonerName === profileName ? (win ? "is-profile-owner_win" : "is-profile-owner_loss") : "",
  };

  return (
    <React.Fragment>
      <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
        <MobileSummonerRow {...props} rowProps={rowProps} />
      </MediaQuery>
      <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
        <DesktopSummonerRow {...props} rowProps={rowProps} />
      </MediaQuery>
    </React.Fragment>
  );
};

const SummonerRowHeader = (props) => {
  const { teamName, win, showPerfScore } = props;
  const teamTextClassName = win ? "" : "red";
  const statusText = win ? "Victory" : "Defeat";
  return (
    <div className={classnames("row-header", { "row-header_hide-perf": !showPerfScore })}>
      <div>
        <span className={`status-label ${teamTextClassName}`}>{statusText}&nbsp;</span>({teamName} Side)
      </div>
      {showPerfScore && <div>Carry</div>}
      <div>KDA</div>
      <div>Damage</div>
      <div>Gold</div>
      <div className="cs-header">CS</div>
      <div className="wards-header">Wards</div>
      <div>
        <div className="post-items-header">Items</div>
      </div>
    </div>
  );
};

const PostGameTeamSection = (props) => {
  const { version, postGameData, allPlayerRanks, seasonId, queueType, winningTeam, regionId, profileName, maxDamage, data } =
    props;

  const patch = normalizePatch(version);
  const isWin = postGameData[0].teamId === winningTeam;
  const teamName = postGameData[0].teamId && postGameData[0].teamId === 100 ? "Blue" : "Red";
  const { matchDuration, matchId } = props.data.matchSummary;
  const showPerfScore = hasPerfScore(queueType);

  return (
    <div className="expanded-match-card-team-combined">
      <SummonerRowHeader teamName={teamName} win={isWin} showPerfScore={showPerfScore} />
      <div className={`expanded-match-card-team ${teamName.toLowerCase()}`}>
        {postGameData.map((data, index) => {
          const defaultQueueType = "ranked_solo_5x5";
          const possibleRanks = ["ranked_solo_5x5", "ranked_flex_sr"];
          const defaultRank = { rank: "i", tier: "unranked" };
          let rankIndex = -1;
          allPlayerRanks.forEach((rank, ind) => {
            if (rank.summonerName === data.summonerName) {
              rankIndex = ind;
            }
          });
          let currentRank = defaultRank;
          if (rankIndex !== -1) {
            if (!possibleRanks.includes(queueType)) {
              currentRank = selectRank(defaultQueueType, Number(seasonId), allPlayerRanks[rankIndex].rankScores);
            } else {
              currentRank = selectRank(queueType, Number(seasonId), allPlayerRanks[rankIndex].rankScores);
            }
          }

          return (
            <SummonerRow
              key={index}
              teamInfo={data}
              showPerfScore={showPerfScore}
              rank={currentRank || defaultRank}
              matchDuration={matchDuration}
              matchId={matchId}
              maxDamage={maxDamage}
              regionId={regionId}
              profileName={profileName}
              win={isWin}
              patch={patch}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PostGameTeamSection;
